const SESSION_EXPIRED_FLAG = "session_expired";

let isRedirecting = false;

export function isValidReturnTo(path: string | null | undefined): boolean {
  if (!path || path.length === 0) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("://")) return false;
  if (path.toLowerCase().startsWith("javascript:")) return false;
  if (path.startsWith("/api/")) return false;
  return true;
}

export function buildLoginRedirectUrl(currentPath: string): string {
  const validated = isValidReturnTo(currentPath) ? currentPath : null;
  const base = "/login";
  return validated ? `${base}?returnTo=${encodeURIComponent(validated)}` : base;
}

/**
 * Handles 401 responses by redirecting to /login with returnTo preservation.
 *
 * Returns `true` if a redirect was initiated (or is already in progress),
 * meaning the caller should NOT throw/surface the error.
 * Returns `false` if the redirect was skipped (e.g., already on /login),
 * meaning the caller should fall through to normal error handling.
 *
 * Dedup guard: only the first concurrent 401 triggers a redirect.
 * The guard resets on full page navigation (module scope unloads).
 */
export function handle401Redirect(): boolean {
  if (isRedirecting) return true;

  const isAlreadyOnLogin = window.location.pathname === "/login";
  if (isAlreadyOnLogin) {
    return false;
  }

  isRedirecting = true;

  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  } catch {
    // sessionStorage may be unavailable (private browsing, etc.)
  }

  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  const target = buildLoginRedirectUrl(currentPath);
  window.location.href = target;
  return true;
}

export function consumeSessionExpiredFlag(): boolean {
  try {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_FLAG);
    if (flag) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      return true;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return false;
}

export { SESSION_EXPIRED_FLAG };

/** Test-only: reset the dedup guard between test cases. */
export function _resetRedirectGuard(): void {
  isRedirecting = false;
}
